<template>
    <section>
      <Filters
        class="data-filters"
        @change="filtersUpdated"
      />
      <div
        v-if="loading"
        class="loader"
      >
        <Loader />
      </div>
      <div v-else>
        <table
          v-if="this.pages.length"
          class="data-table"
        >
          <thead>
            <tr class="data-table__row data-table__head">
              <th>#</th>
              <th>Summoner name</th>
              <th>Country</th>
              <th>Location</th>
              <th>Tier</th>
              <th>LP</th>
              <th>Server</th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="(summoner, index) of pages[currentPageNumber]"
            >
              <tr
                :key="summoner.id"
                class="data-table__row summoner"
              >
                <td class="summoner__rank">{{ index + 1 + (currentPageNumber * summonersPerPage) }}</td>
                <td class="summoner__name">{{ summoner.name }}</td>
                <td class="summoner__country">{{ summoner.country }}</td>
                <td class="summoner__city">{{ summoner.city }}</td>
                <td
                  v-if="summoner.tier"
                  class="summoner__tier"
                >
                  <span :class="summoner.tier" />
                  <span>{{ summoner.tier }} {{ summoner.rank }}</span>
                </td>
                <td
                  v-else
                  class="summoner__unranked"
                >
                  unranked
                </td>
                <td class="summoner__lp">{{ summoner.leaguePoints }}</td>
                <td class="summoner__server">{{ serverName(summoner.server) }}</td>
              </tr>
              <tr
                v-if="summoner.about"
                :key="summoner.name"
                class="summoner__about-wrap" 
              >
                <td class="summoner__about-filler"></td>
                <td
                  class="summoner__about"  
                  colspan="7"
                >
                  {{ summoner.about }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div class="buttons">
          <button
            v-if="currentPageNumber !== 0"
            class="tf-button tf-button--secondary"
            @click="previous"
          >
            Previous
          </button>
          <button
            v-if="!lastPage"
            class="tf-button tf-button--secondary"
            @click="next"
          >
            Next
          </button>
        </div>
        <div class="no-results" v-if="this.pages.length && !this.pages[0].length">
          <img
            src="@/assets/images/UnsealedSpellbook.png"
            width="256"
            height="256"
            class="header__image"
            alt="unsealed spellbook"
          >
          <p>No summoners match the selected criteria, you can change the filters above.</p>
        </div>
        <p class="update-info" v-if="this.pages.length && this.pages[0].length">
          <span class="update-info__icon">🛈</span> Ranked data is automatically updated every 12 hours.
        </p>
      </div>
    </section>
</template>

<script>
import { db } from '@/main'
import Filters from './Filters.vue'
import Loader from './Loader.vue'
import axios from 'axios'
import { servers, refreshLimit, cloudFunctionBaseUrl } from '../constants'

export default {
  name: 'SummonerList',

  components: {
    Filters,
    Loader
  },

  data() {
    return {
      pages: [],
      summonersPerPage: 20,
      currentPageNumber: 0,
      lastDocument: undefined,
      loading: false,
      server: '',
      country: '',
      city: '',
      area: ''
    }
  },

  computed: {
    lastPage() {
      return !this.pages.length || (this.pages[this.currentPageNumber] && this.pages[this.currentPageNumber].length < this.summonersPerPage)
    }
  },

  methods: {
    previous() {
      if (this.currentPageNumber) {
        this.currentPageNumber--
      }
    },

    async next() {
      const nextPageNumber = this.currentPageNumber + 1
      const nextPageLoaded = this.pages[nextPageNumber]

      if (nextPageLoaded) {
        this.currentPageNumber++
      } else {
        this.loadSummoners(true)
      }
    },

    serverName(serverId) {
      const server = servers.find(server => server.value === serverId)

      return server ? server.name : serverId
    },

    async refreshSummoner(summoner) {
      const date = new Date()
      const now = date.getTime()
      const timeSinceLastUpdate = now - summoner.lastUpdate
      if (timeSinceLastUpdate > refreshLimit) {
        try {
          const response = await axios.get(`${cloudFunctionBaseUrl}refreshSummoner?id=${summoner.id}&server=${summoner.server}`)
        } catch (error) {}
      }
    },

    filtersUpdated({ server, country, city, area }) {
      this.server = server
      this.country = country
      this.city = city
      this.area = area
      this.lastDocument = undefined
      this.currentPageNumber = 0
      this.pages = []
      this.loadSummoners()
    },

    async loadSummoners(next = false) {
      if (this.loading) {
        return
      }
      this.loading = true

      let query = db.collection('summoners')
  
      if (this.server) {
        query = query.where('server', '==', this.server)
      }

      if (this.country) {
        query = query.where('country', '==', this.country)
      }

      if (this.city) {
        query = query.where('city', '==', this.city)
      }

      if (this.area) {
        query = query.where('area', '==', this.area)
      }

      query = query.orderBy('score', 'desc')

      if (next && this.lastDocument) {
        this.currentPageNumber++
        query = query.startAfter(this.lastDocument)
      }

      query = query.limit(this.summonersPerPage)

      try {
        const documentSnapshots = await query.get()

        if (!documentSnapshots.empty) {
          this.lastDocument = documentSnapshots.docs[documentSnapshots.docs.length - 1]
        }
        const page = []
        documentSnapshots.forEach(summoner => {
          const data = summoner.data()
          this.refreshSummoner(data)
          page.push(data)
        })
        this.noMorePages = documentSnapshots.length < this.summonersPerPage
        this.pages = this.pages.concat([page])
      } catch (error) {} finally {
        this.loading = false
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.data-table {
  width: 100%;
  border-spacing: 0;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      align-items: baseline;
      grid-template-areas: 'rank name name name'
      'country country city city'
      'tier tier lp server'
      
    }

    &__head {
      display: none;
    }
  }
}

.summoner {
  &:hover {
    background-color: #ddd;
  }
  &__rank {
    font-family: var(--head-font);
    font-size: 18px;
    grid-area: rank;
  }
  &__name {
    font-weight: bold;
    grid-area: name;
  }

  &__country {
    grid-area: country;
  }

  &__city {
    grid-area: city;
  }

  &__tier {
    font-size: 14px;
    color: #666;
    grid-area: tier;
  }

  &__unranked {
    color: #999;
    font-size: 14px;
    font-style: italic;
    grid-area: tier;
  }

  &__lp {
    grid-area: lp
  }

  &__server {
    grid-area: server;
  }

  &__about {
    background-color: #e5e5e5;
    position: relative;
    word-break: break-all;
    word-break: break-word;

    &::after {
      content: '';
      position: absolute;
      top: -7px;
      left: 20px;
      width: 15px;
      background-color: #e5e5e5;
      height: 15px;
      transform: rotate(45deg);
    }
  }

  &__about-filler {
    @media (max-width: 500px) {
      display: none;
    }
  }
}

.buttons {
  text-align: center;
  margin-bottom: 40px;
}

.buttons :first-child {
  margin-right: 15px;
}

.data-filters {
  margin-bottom: 25px;
}

.update-info {
  font-size: 12px;
  color: #999;
  margin: 20px 0;
  text-align: center;

  &__icon {
    font-size: 18px;
    line-height: 1.1;
    position: relative;
    top: 2px;
  }
}

.loader {
  display: flex;
  justify-content: center;
  padding: 5vmin 0;
}

.no-results {
  text-align: center;
  font-style: italic;
  font-size: 16px;
  font-family: var(--head-font);
  padding: 20px 0;
}
</style>
